import { atom } from 'jotai';
import request from 'lib/request';
import { InterpretationCardResponse, SearchResult } from 'types/api-responses';
import { RunsheetGraphDataPoints, extractRunsheetData } from './utils';
import { GraphEntity } from './gojs-graph';

export type RunsheetQueryResult = SearchResult<InterpretationCardResponse>;

// The backend clamps `page` to MAX_RESULT_WINDOW / page_size; a page past that
// window re-serves the last valid page's rows. Never request those pages, or the
// graph silently double-counts. Mirrors query/interpretations.rb.
const MAX_RESULT_WINDOW = 10_000;

async function fetchRunsheetPage(
  runsheetQueryHref: string,
  page: number,
  abortController: AbortController
): Promise<RunsheetQueryResult> {
  const response = await request.post<RunsheetQueryResult, unknown>(
    runsheetQueryHref,
    {
      page: page,
      pageSize: 100,
      sortAttribute: 'date',
      sortDirection: 'ASC',
    },
    abortController
  );

  if (response.type === 'success') {
    return response.data;
  }

  throw new Error('Could not fetch all runsheet data');
}

const fetchAllRunsheetPages = async (
  runsheetQueryHref: string,
  abortController: AbortController
) => {
  const firstPageData = await fetchRunsheetPage(
    runsheetQueryHref,
    1,
    abortController
  );
  const totalCount = firstPageData.totalCount;
  const pageSize = firstPageData.pageSize;
  const totalPages = Math.ceil(totalCount / pageSize);

  // Fetch only pages the backend won't clamp; anything past the window would
  // re-serve the last page. When we stop short, report it rather than pretend.
  const maxPages = Math.max(1, Math.floor(MAX_RESULT_WINDOW / pageSize));
  const fetchablePages = Math.min(totalPages, maxPages);
  const truncated = totalPages > maxPages;

  if (fetchablePages <= 1) {
    return { results: firstPageData.results, truncated };
  }

  const remainingPageRequests = [];
  for (let page = 2; page <= fetchablePages; page++) {
    remainingPageRequests.push(
      fetchRunsheetPage(runsheetQueryHref, page, abortController)
    );
  }

  const remainingPageData = await Promise.all(remainingPageRequests);
  const results = [
    firstPageData.results,
    ...remainingPageData.map((el) => el.results),
  ].flat();

  return { results, truncated };
};

const createRunsheetAtom = (runsheetQueryHref: string | undefined) => {
  const baseAtom = atom<RunsheetGraphDataPoints | undefined>(undefined);
  // Retained so a re-fetch or unmount can abort the in-flight page requests
  // instead of leaving up to ~99 POSTs running after the graph is gone.
  let inFlight: AbortController | undefined;

  const fetchRunsheetAtom = atom<
    RunsheetGraphDataPoints | undefined,
    [undefined],
    void
  >(
    (get) => {
      return get(baseAtom);
    },
    async (_get, set) => {
      if (!runsheetQueryHref) return;
      inFlight?.abort();
      const controller = new AbortController();
      inFlight = controller;
      try {
        const { results, truncated } = await fetchAllRunsheetPages(
          runsheetQueryHref,
          controller
        );
        if (controller.signal.aborted) return;
        const points = extractRunsheetData(results);
        set(baseAtom, { ...points, truncated });
      } catch (error) {
        // An aborted fetch is expected on re-fetch/unmount; only surface real errors.
        if (controller.signal.aborted) return;
        throw error;
      }
    }
  );
  fetchRunsheetAtom.onMount = (set) => {
    set(undefined);
    return () => inFlight?.abort();
  };

  return fetchRunsheetAtom;
};

export type GraphEntityType =
  'no-transactions-in' | 'transactions-in-out' | 'no-transactions-out';

const graphEntitiesAtom = atom<{
  type: GraphEntityType | null;
  entities: GraphEntity[];
}>({
  type: null,
  entities: [],
});

const nodeClickedAtom = atom<boolean>(false);

export {
  createRunsheetAtom,
  fetchAllRunsheetPages,
  graphEntitiesAtom,
  nodeClickedAtom,
};
